import {
    Box,
    CardActions,
    CardContent,
    IconButton,
    Typography,
} from "@mui/material";
import React from "react";
import { DownloadOutlined } from "@mui/icons-material";
import { UsersResponseLabel } from "../../users";
import {
    TableCell,
    TableEmptyState,
    TableFooterPagination,
    TableHeaderRow,
    TableRow,
} from "../table_toolkit/table_toolkit";
import { black } from "../../../../theme/colors";

interface DataGridInterface {
    labels: UsersResponseLabel[];
    data: {
        adminEmailId: string,
        adminId: number,
        adminName: string,
        choiceName: string,
        id: number,
        processedAt: string,
        status: string,
        recordCount: number;
        remark: { details: string, message: string }
    }[];
    count: number;
    page: number;
    setPage: (val: number) => void;
    downloadResponse: (taskId: number) => void
}

const STATUS_STYLES: Record<string, { color: string; bgcolor: string }> = {
    Pending: { color: "#91278F", bgcolor: "#F5E6F5" },
    Completed: { color: "#1E6446", bgcolor: "#E7F5EC" },
    Failed: { color: "#D14C2E", bgcolor: "#FAEDEA" },
};

function StatusChip({ status }: { status: string }) {
    const style = STATUS_STYLES[status] ?? { color: black, bgcolor: "#F0F0F0" };
    return (
        <Box
            sx={{
                display: "inline-flex",
                alignItems: "center",
                bgcolor: style.bgcolor,
                color: style.color,
                borderRadius: 1.5,
                px: 1.25,
                py: 0.375,
                fontSize: 12.5,
                fontWeight: 700,
            }}
        >
            {status}
        </Box>
    );
}

export default function DataGridDownloads({
    labels,
    data,
    page,
    setPage,
    count,
    downloadResponse
}: DataGridInterface) {

    const startRange = (page - 1) * 8 + 1;
    const endRange = Math.min((page) * 8, count);
    const handleChangePage = (
        event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number
    ) => {
        setPage(newPage + 1);
    };

    const customText = `Showing ${startRange} to ${endRange} of ${count} entries`;

    return (
        <Box
            display="flex"
            flex={1}
            flexDirection={"column"}
            height={"100%"}
            width={"100%"}
        >
            <CardContent
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    flexGrow: 1,
                    p: 0,
                    overflowX: "auto",
                }}
            >
                <Box sx={{ minWidth: { xs: 0, sm: 840 } }}>
                <TableHeaderRow labels={labels} align="center" bordered />
                {data.length === 0 ? (
                    <TableEmptyState message="No downloads yet" />
                ) : (
                    <Box display="flex" flexDirection="column">
                        {data?.map((item, index) => (
                            <TableRow key={item?.id ?? index} bordered>
                                <TableCell label="SL. No" bordered>
                                    <Typography display="flex" justifyContent={{ xs: "flex-start", sm: "center" }} fontSize={13.5}>
                                        {index + 1}
                                    </Typography>
                                </TableCell>
                                <TableCell label="Status" bordered>
                                    <Box display="flex" justifyContent={{ xs: "flex-start", sm: "center" }}>
                                        <StatusChip status={item?.status} />
                                    </Box>
                                </TableCell>
                                <TableCell label="Remark" bordered>
                                    <Typography display="flex" justifyContent={{ xs: "flex-start", sm: "center" }} fontSize={13.5} noWrap>
                                        {item?.remark?.message || "-"}
                                    </Typography>
                                </TableCell>
                                <TableCell label="Processed By" bordered>
                                    <Typography display="flex" justifyContent={{ xs: "flex-start", sm: "center" }} fontSize={13.5} noWrap>
                                        {item?.adminName}
                                    </Typography>
                                </TableCell>
                                <TableCell label="Processed At" bordered>
                                    <Typography display="flex" justifyContent={{ xs: "flex-start", sm: "center" }} fontSize={13.5} noWrap>
                                        {item?.processedAt}
                                    </Typography>
                                </TableCell>
                                <TableCell label="Record Count" bordered>
                                    <Typography display="flex" justifyContent={{ xs: "flex-start", sm: "center" }} fontSize={13.5}>
                                        {item?.recordCount}
                                    </Typography>
                                </TableCell>
                                <TableCell label="Action" isLast bordered>
                                    <Box display="flex" justifyContent={{ xs: "flex-start", sm: "center" }}>
                                        <IconButton
                                            disabled={item?.status !== 'Completed'}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                downloadResponse(item?.id);
                                            }}
                                            sx={{ "&:hover": { bgcolor: "#F5E6F5" } }}
                                        >
                                            <DownloadOutlined color={item?.status === 'Completed' ? "primary" : "disabled"} fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                    </Box>
                )}
                </Box>
            </CardContent>
            <CardActions sx={{ px: 2, py: 2 }}>
                <TableFooterPagination
                    count={count}
                    page={page - 1}
                    onPageChange={handleChangePage}
                    summaryText={customText}
                />
            </CardActions>
        </Box>
    );
}
